import React, { useState, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Checkbox,
    Collapse,
    IconButton,
    TextField,
    Button,
    Chip,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    LinearProgress,
    Tooltip,
    Snackbar,
    Alert,
} from "@mui/material";
import {
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckCircleIcon,
    MonitorHeart as VitalIcon,
    Spa as BowelIcon,
    Bathtub as BodyCareIcon,
    FaceRetouchingNatural as SkinCareIcon,
    Restaurant as MealsIcon,
    Hotel as BedIcon,
    Category as OtherIcon,
    Save as SaveIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
} from "@mui/icons-material";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Task {
    id: string;
    label: string;
    checked: boolean;
    note?: string;          // optional free-text
    percentage?: number;    // for Meals
    hasPercentage?: boolean;
    hasNote?: boolean;
    isCustom?: boolean;
}

interface TaskCategory {
    key: string;
    label: string;
    icon: React.ReactNode;
    color: string;
    tasks: Task[];
    expanded: boolean;
    allowAddTask?: boolean;
}

const SHIFTS = ["Morning", "Afternoon", "Evening", "Night"];

const FACILITIES = [
    "Fountainview Assisted Living",
    "Sunrise Care Center",
    "Meadowbrook Memory Care",
];

const buildId = (prefix: string, label: string) =>
    `${prefix}-${label.toLowerCase().replace(/\s+/g, "-")}`;

const makeTask = (
    prefix: string,
    label: string,
    extra: Partial<Task> = {}
): Task => ({
    id: buildId(prefix, label),
    label,
    checked: false,
    ...extra,
});

// ─── Initial category data ────────────────────────────────────────────────────
const buildInitialCategories = (): TaskCategory[] => [
    {
        key: "vitals",
        label: "Vital Signs",
        icon: <VitalIcon />,
        color: "#4f46e5",
        expanded: true,
        tasks: [
            makeTask("v", "Weight"),
            makeTask("v", "Temperature"),
            makeTask("v", "Pulse"),
            makeTask("v", "Respiration"),
            makeTask("v", "Blood Pressure"),
            makeTask("v", "Oxygen Level"),
            makeTask("v", "Blood Sugar Level"),
        ],
    },
    {
        key: "bowel",
        label: "Bowel",
        icon: <BowelIcon />,
        color: "#0891b2",
        expanded: true,
        allowAddTask: true,
        tasks: [
            makeTask("b", "Bowel", { hasNote: true }),
            makeTask("b", "Urinary"),
            makeTask("b", "Catheter/drain"),
            makeTask("b", "Incontinence Care"),
        ],
    },
    {
        key: "bodycare",
        label: "Body Care",
        icon: <BodyCareIcon />,
        color: "#059669",
        expanded: true,
        tasks: [
            makeTask("bc", "Full bath"),
            makeTask("bc", "Shampoo/Soap"),
            makeTask("bc", "Partial Bath"),
            makeTask("bc", "Shave"),
            makeTask("bc", "Comb Hair"),
            makeTask("bc", "Oral Care"),
            makeTask("bc", "Assist with clothing"),
            makeTask("bc", "Hand Nail Care"),
            makeTask("bc", "Toe Nail Care"),
        ],
    },
    {
        key: "skincare",
        label: "Skin Care",
        icon: <SkinCareIcon />,
        color: "#db2777",
        expanded: true,
        tasks: [
            makeTask("sc", "Apply Skin Barrier"),
            makeTask("sc", "Pedicure"),
            makeTask("sc", "Lower Extremities"),
            makeTask("sc", "Apply Lotion"),
            makeTask("sc", "Elevate Leg"),
        ],
    },
    {
        key: "meals",
        label: "Meals",
        icon: <MealsIcon />,
        color: "#d97706",
        expanded: true,
        tasks: [
            makeTask("m", "Breakfast", { hasPercentage: true }),
            makeTask("m", "Lunch", { hasPercentage: true }),
            makeTask("m", "Dinner", { hasPercentage: true }),
        ],
    },
    {
        key: "bed",
        label: "Bed",
        icon: <BedIcon />,
        color: "#7c3aed",
        expanded: true,
        tasks: [
            makeTask("bd", "Medication"),
            makeTask("bd", "Mood"),
            makeTask("bd", "Night Checkup"),
        ],
    },
    {
        key: "other",
        label: "Other",
        icon: <OtherIcon />,
        color: "#6b7280",
        expanded: true,
        allowAddTask: true,
        tasks: [makeTask("o", "Snack/Water")],
    },
];

// ─── Component ─────────────────────────────────────────────────────────────────
const CaregiverTasksPage: React.FC = () => {
    const [categories, setCategories] = useState<TaskCategory[]>(buildInitialCategories);
    const [shift, setShift] = useState("Morning");
    const [facility, setFacility] = useState("Fountainview Assisted Living");
    const [residentName] = useState("Valentine Test");
    const [caregiverName] = useState("Jessie Tonda");
    const [addingTask, setAddingTask] = useState<{ catKey: string; value: string } | null>(null);
    const [saved, setSaved] = useState(false);

    const totalTasks = categories.flatMap((c) => c.tasks).length;
    const completedTasks = categories.flatMap((c) => c.tasks).filter((t) => t.checked).length;
    const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    // ── Helpers ──
    const updateTask = useCallback(
        (catKey: string, taskId: string, patch: Partial<Task>) => {
            setCategories((prev) =>
                prev.map((cat) =>
                    cat.key === catKey
                        ? {
                            ...cat,
                            tasks: cat.tasks.map((t) =>
                                t.id === taskId ? { ...t, ...patch } : t
                            ),
                        }
                        : cat
                )
            );
        },
        []
    );

    const toggleCategory = (catKey: string) => {
        setCategories((prev) =>
            prev.map((cat) =>
                cat.key === catKey ? { ...cat, expanded: !cat.expanded } : cat
            )
        );
    };

    const addCustomTask = (catKey: string, label: string) => {
        if (!label.trim()) return;
        setCategories((prev) =>
            prev.map((cat) =>
                cat.key === catKey
                    ? {
                        ...cat,
                        tasks: [
                            ...cat.tasks,
                            makeTask(`custom-${catKey}`, label.trim(), { isCustom: true }),
                        ],
                    }
                    : cat
            )
        );
        setAddingTask(null);
    };

    const removeTask = (catKey: string, taskId: string) => {
        setCategories((prev) =>
            prev.map((cat) =>
                cat.key === catKey
                    ? { ...cat, tasks: cat.tasks.filter((t) => t.id !== taskId) }
                    : cat
            )
        );
    };

    const handleSave = () => {
        // In production, POST to backend here
        setSaved(true);
    };

    // ── Render a task row ──
    const renderTask = (cat: TaskCategory, task: Task) => (
        <Box key={task.id}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 0.6,
                    px: 1,
                    borderRadius: 1,
                    cursor: "pointer",
                    transition: "background 0.15s",
                    "&:hover": { bgcolor: `${cat.color}08` },
                }}
                onClick={() => updateTask(cat.key, task.id, { checked: !task.checked })}
            >
                <Checkbox
                    checked={task.checked}
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => updateTask(cat.key, task.id, { checked: !task.checked })}
                    sx={{
                        p: 0.25,
                        color: cat.color,
                        "&.Mui-checked": { color: cat.color },
                    }}
                />
                <Typography
                    variant="body2"
                    sx={{
                        flexGrow: 1,
                        fontWeight: task.checked ? 600 : 400,
                        color: task.checked ? cat.color : "text.primary",
                        textDecoration: task.checked ? "none" : "none",
                        fontSize: "0.875rem",
                    }}
                >
                    {task.label}
                </Typography>
                {task.isCustom && (
                    <Tooltip title="Remove">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                removeTask(cat.key, task.id);
                            }}
                            sx={{ opacity: 0.4, "&:hover": { opacity: 1, color: "error.main" } }}
                        >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                )}
                {task.checked && (
                    <CheckCircleIcon sx={{ fontSize: 16, color: cat.color, opacity: 0.8 }} />
                )}
            </Box>

            {/* Percentage input for Meals */}
            {task.hasPercentage && task.checked && (
                <Box sx={{ ml: 4.5, mb: 0.5 }}>
                    <TextField
                        size="small"
                        placeholder="Intake %"
                        value={task.percentage ?? ""}
                        onChange={(e) => {
                            const val = parseInt(e.target.value);
                            updateTask(cat.key, task.id, {
                                percentage: isNaN(val) ? undefined : Math.min(100, Math.max(0, val)),
                            });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        type="number"
                        inputProps={{ min: 0, max: 100 }}
                        sx={{
                            width: 180,
                            "& .MuiInputBase-root": { height: 30, fontSize: "0.8rem" },
                        }}
                        InputProps={{
                            endAdornment: (
                                <Typography variant="caption" sx={{ mr: 1, color: "text.secondary" }}>
                                    %
                                </Typography>
                            ),
                        }}
                    />
                </Box>
            )}

            {/* Free-text note (e.g., Bowel) */}
            {task.hasNote && (
                <Box sx={{ ml: 4.5, mb: 0.5 }}>
                    <TextField
                        size="small"
                        placeholder="Add Text"
                        value={task.note ?? ""}
                        onChange={(e) =>
                            updateTask(cat.key, task.id, { note: e.target.value })
                        }
                        onClick={(e) => e.stopPropagation()}
                        fullWidth
                        sx={{
                            maxWidth: 300,
                            "& .MuiInputBase-root": { height: 30, fontSize: "0.8rem" },
                        }}
                    />
                </Box>
            )}
        </Box>
    );

    // ── Render a category card ──
    const renderCategory = (cat: TaskCategory) => {
        const completed = cat.tasks.filter((t) => t.checked).length;
        const total = cat.tasks.length;
        const allDone = total > 0 && completed === total;

        return (
            <Paper
                key={cat.key}
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    borderColor: allDone ? cat.color : "divider",
                    transition: "border-color 0.25s",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1.2,
                        bgcolor: allDone ? `${cat.color}12` : "grey.50",
                        cursor: "pointer",
                        userSelect: "none",
                        transition: "background 0.2s",
                    }}
                    onClick={() => toggleCategory(cat.key)}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ color: cat.color, display: "flex", alignItems: "center" }}>
                            {cat.icon}
                        </Box>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: cat.color }}>
                            {cat.label}
                        </Typography>
                        <Chip
                            label={`${completed}/${total}`}
                            size="small"
                            sx={{
                                height: 18,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                bgcolor: allDone ? cat.color : "grey.200",
                                color: allDone ? "white" : "text.secondary",
                            }}
                        />
                    </Box>
                    <IconButton size="small">
                        {cat.expanded ? (
                            <ExpandLessIcon fontSize="small" />
                        ) : (
                            <ExpandMoreIcon fontSize="small" />
                        )}
                    </IconButton>
                </Box>

                {/* Task list */}
                <Collapse in={cat.expanded}>
                    <Box sx={{ px: 1, pt: 0.5, pb: 1 }}>
                        {cat.tasks.map((task) => renderTask(cat, task))}

                        {/* Add task row */}
                        {cat.allowAddTask && (
                            <>
                                {addingTask?.catKey === cat.key ? (
                                    <Box sx={{ display: "flex", gap: 1, mt: 1, px: 0.5 }}>
                                        <TextField
                                            size="small"
                                            autoFocus
                                            placeholder="Task name..."
                                            value={addingTask.value}
                                            onChange={(e) =>
                                                setAddingTask({ catKey: cat.key, value: e.target.value })
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter")
                                                    addCustomTask(cat.key, addingTask.value);
                                                if (e.key === "Escape") setAddingTask(null);
                                            }}
                                            sx={{ flexGrow: 1, "& .MuiInputBase-root": { height: 30, fontSize: "0.8rem" } }}
                                        />
                                        <Button
                                            size="small"
                                            variant="contained"
                                            sx={{ minWidth: 0, px: 1.5, height: 30, bgcolor: cat.color }}
                                            onClick={() => addCustomTask(cat.key, addingTask.value)}
                                        >
                                            Add
                                        </Button>
                                        <Button
                                            size="small"
                                            sx={{ minWidth: 0, px: 1, height: 30 }}
                                            onClick={() => setAddingTask(null)}
                                        >
                                            ✕
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            mt: 0.5,
                                            px: 1,
                                            py: 0.4,
                                            opacity: 0.5,
                                            cursor: "pointer",
                                            borderRadius: 1,
                                            "&:hover": { opacity: 1, bgcolor: `${cat.color}10` },
                                        }}
                                        onClick={() => setAddingTask({ catKey: cat.key, value: "" })}
                                    >
                                        <AddIcon sx={{ fontSize: 14, color: cat.color }} />
                                        <Typography variant="caption" sx={{ color: cat.color, fontWeight: 500 }}>
                                            Add task
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </Collapse>
            </Paper>
        );
    };

    return (
        <Box sx={{ pb: 6 }}>
            {/* ── Page header ── */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    gap: { xs: 2, sm: 0 },
                    mb: 2,
                }}
            >
                <Typography variant="h5" fontWeight={700} component="h1">
                    Add Task
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    sx={{
                        bgcolor: "#4f46e5",
                        "&:hover": { bgcolor: "#4338ca" },
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                    Save Tasks
                </Button>
            </Box>

            {/* ── Context strip ── */}
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 2, sm: 3 },
                    flexWrap: "wrap",
                }}
            >
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                        Resident
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                        {residentName}
                    </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                        Caregiver
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                        {caregiverName}
                    </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 250 }, flexGrow: { xs: 1, sm: 0 } }}>
                    <InputLabel>Facility</InputLabel>
                    <Select
                        value={facility}
                        label="Facility"
                        onChange={(e) => setFacility(e.target.value)}
                    >
                        {FACILITIES.map((f) => (
                            <MenuItem key={f} value={f}>
                                {f}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 140 }, flexGrow: { xs: 1, sm: 0 } }}>
                    <InputLabel>Shift</InputLabel>
                    <Select
                        value={shift}
                        label="Shift"
                        onChange={(e) => setShift(e.target.value)}
                    >
                        {SHIFTS.map((s) => (
                            <MenuItem key={s} value={s}>
                                {s}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Paper>

            {/* ── Progress bar ── */}
            <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Overall Completion
                    </Typography>
                    <Typography variant="caption" fontWeight={700} color="#4f46e5">
                        {completedTasks}/{totalTasks} tasks · {progress}%
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "grey.100",
                        "& .MuiLinearProgress-bar": {
                            bgcolor: progress === 100 ? "#059669" : "#4f46e5",
                            borderRadius: 4,
                        },
                    }}
                />
            </Box>

            {/* ── Task List label ── */}
            <Typography
                variant="h6"
                fontWeight={700}
                sx={{ mb: 2, color: "text.primary" }}
            >
                Task List
            </Typography>

            {/* ── Category grid ── */}
            <Grid container spacing={2}>
                {categories.map((cat) => (
                    <Grid item xs={12} sm={6} lg={3} key={cat.key}>
                        {renderCategory(cat)}
                    </Grid>
                ))}
            </Grid>

            {/* ── Bottom Save ── */}
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    sx={{
                        bgcolor: "#4f46e5",
                        "&:hover": { bgcolor: "#4338ca" },
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        px: 4,
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                    Save & Submit Tasks
                </Button>
            </Box>

            {/* ── Success snackbar ── */}
            <Snackbar
                open={saved}
                autoHideDuration={3000}
                onClose={() => setSaved(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert severity="success" onClose={() => setSaved(false)}>
                    Tasks saved successfully!
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CaregiverTasksPage;
